// "Scores on pins": when zoomed in far enough over few enough weather cells,
// draw the current-hour score on each pub in view. One multi-coordinate
// request covers every cell, through the cache and within the session cap.

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { FeatureCollection } from 'geojson';

import { PINS_MAX_CELLS, PINS_MAX_PUBS, PINS_MIN_ZOOM, WEATHER_CELL_DEG } from '../data/liveConfig';
import { summary, type PubStore } from '../data/pubs';
import type { MapViewHandle } from './MapView';

/** How many weather cells a box touches. */
export function cellsInBox(south: number, west: number, north: number, east: number): number {
  const index = (v: number) => Math.round(v / WEATHER_CELL_DEG);
  return (index(north) - index(south) + 1) * (index(east) - index(west) + 1);
}

const MOVE_DEBOUNCE_MS = 400;

export function useScorePins(
  mapRef: RefObject<MapViewHandle | null>,
  store: PubStore | null,
  enabled: boolean,
) {
  const [pins, setPins] = useState<FeatureCollection | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const generation = useRef(0);

  const update = useCallback(() => {
    const run = ++generation.current;
    const view = mapRef.current?.viewport();
    if (!enabled || !store || !view) {
      setPins(null);
      setHint(null);
      return;
    }
    if (view.zoom < PINS_MIN_ZOOM) {
      setPins(null);
      setHint('Zoom in closer to see scores on the pins.');
      return;
    }
    if (cellsInBox(view.south, view.west, view.north, view.east) > PINS_MAX_CELLS) {
      setPins(null);
      setHint('Zoom in a little more to see scores on the pins.');
      return;
    }
    const { indices } = mapRef.current?.pubsInView(PINS_MAX_PUBS) ?? { indices: [] };
    if (indices.length === 0) {
      setPins(null);
      setHint(null);
      return;
    }
    const pubs = indices.map((index) => summary(store, index));
    setHint('Fetching scores…');
    // The live-data and scoring code is its own chunk, loaded on first use.
    import('../data/live')
      .then(({ loadAreaScores }) => loadAreaScores(pubs, { full: false }))
      .then(({ scores: scored, problem }) => {
        if (run !== generation.current) return;
        setPins({
          type: 'FeatureCollection',
          features: scored.map(({ index, now: score }) => ({
            type: 'Feature',
            // x: the pub is shut this hour (the pin is dimmed).
            properties: { s: score.total, b: score.band, x: score.open === false ? 1 : 0 },
            geometry: {
              type: 'Point',
              coordinates: [store.lon[index] ?? 0, store.lat[index] ?? 0],
            },
          })),
        });
        setHint(
          problem === 'capped'
            ? 'Session weather limit reached, so some pins have no score. Tap a pub for its forecast.'
            : problem === 'failed'
              ? 'Couldn’t get the weather for every pin just now.'
              : `Scores for this hour on ${scored.length} pub${scored.length === 1 ? '' : 's'}.`,
        );
      })
      .catch(() => {
        if (run === generation.current) setHint('Couldn’t get the weather for the pins just now.');
      });
  }, [mapRef, store, enabled]);

  /** Call on map move end: waits for the map to settle. */
  const onMoveEnd = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(update, MOVE_DEBOUNCE_MS);
  }, [update]);

  // Turning pins on (or new data) applies straight away; off hides them.
  useEffect(() => {
    if (!enabled) {
      generation.current++;
      return;
    }
    timer.current = window.setTimeout(update, 0);
    return () => window.clearTimeout(timer.current);
  }, [update, enabled]);

  return { pins: enabled ? pins : null, hint: enabled ? hint : null, onMoveEnd };
}
